import { useSuspenseQuery } from '@tanstack/react-query';
import React from 'react';

import type * as api from '@gitbook/api-client';
import { METER_PRICES } from '@gitbook/app-pricing';
import { BarChart, ChartSkeleton } from '@gitbook/spine-react';

import { apiQuery } from '../../state';

type BillingMeterUsageChartProps = {
    organizationId: string;
    meterName: api.BillingMeter;
};

const CHART_HEIGHT = 200;

/**
 * Display a chart of the usage of a billing meter.
 */
export function BillingMeterUsageChart(props: BillingMeterUsageChartProps) {
    return (
        <div className="w-full">
            <React.Suspense fallback={<ChartSkeleton height={CHART_HEIGHT} />}>
                <BillingMeterUsageChartContent {...props} />
            </React.Suspense>
        </div>
    );
}

function BillingMeterUsageChartContent(props: BillingMeterUsageChartProps) {
    const { organizationId, meterName } = props;

    const { data } = useSuspenseQuery(
        apiQuery('getOrganizationBillingMeterUsage', {
            organizationId,
            meterName,
        })
    );

    // We create 2 series: one for charges included in the subscription and one for charges additional usage.
    const seriesData = React.useMemo(() => {
        const meterPrice = METER_PRICES[meterName];
        let included = meterPrice.startingAt;

        const result: Array<{
            date: Date;
            included: number;
            additional: number;
        }> = [];

        for (const entry of data.entries) {
            if (entry.value <= included) {
                result.push({
                    date: new Date(entry.date),
                    included: entry.value,
                    additional: 0,
                });
                included -= entry.value;
            } else if (included > 0) {
                result.push({
                    date: new Date(entry.date),
                    included: included,
                    additional: entry.value - included,
                });
                included = 0;
            } else {
                result.push({
                    date: new Date(entry.date),
                    included: 0,
                    additional: entry.value,
                });
            }
        }

        return result;
    }, [meterName, data.entries]);

    return (
        <div className="w-full">
            <BarChart
                data={seriesData}
                xDataKey={(item) => {
                    return item.date.toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                    });
                }}
                series={[
                    {
                        id: 'included',
                        dataKey: 'included',
                        name: `${METER_PRICES[meterName].unitLabel} included`,
                        stackId: 'usage',
                    },
                    {
                        id: 'additional',
                        dataKey: 'additional',
                        name: 'Additional usage',
                        stackId: 'usage',
                    },
                ]}
                height={CHART_HEIGHT}
                className="w-full"
            />
        </div>
    );
}
